// L'enveloppe de run : bandeau, rail, corps de l'étape, barre d'action.
//
// C'est ici que le principe fondateur se joue — LES PAGES SONT LES ÉTAPES (P1).
// Il n'y a pas de liste de tâches à côté du travail avec des liens vers des
// outils : tu ouvres l'étape, tu la fais, tu la valides, et l'application
// t'emmène à la suivante.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRun } from '@/data/RunProvider'
import * as api from '@/data/api'
import { ETAPE_PAR_CODE, ETAPES } from '@/domain/steps'
import {
  estActionnable, estDeCote, estSkip, parRang, prochaineEtape, progression,
  reprise, segment,
} from '@/domain/parcours'
import { pret, questionSansObjet, type Pret } from '@/domain/derive'
import { useReglages } from '@/data/reglages'
import { pretSco } from '@/domain/especes'
import { duree, depuis, heure, initiales, pluriel } from '@/lib/format'
import { messageErreur } from '@/lib/errors'
import { aller } from '@/lib/router'
import { useAuth } from '@/lib/auth'

import { Chip, Lamp, Modale, Notice } from '@/ui/primitives'
import { PanneauVersement } from '@/ui/Versement'
import { ETAPE_DU_MODULE, LIBELLE_MODULE, modulesDeLEtape,
         type SessionEnAttente } from '@/domain/hw'
import { useToast } from '@/ui/Toast'
import { ECRANS } from '@/steps'
import type { Etape } from '@/domain/types'

/** « Autre » ouvre la saisie libre : il est ajouté par le code, jamais réglable. */
const AUTRE = 'Autre'

export default function RunView({ codeDemande }: { codeDemande: string | null }) {
  const run = useRun()
  const reglages = useReglages()
  const toast = useToast()
  const { nom } = useAuth()
  const [railOuvert, setRailOuvert] = useState(false)
  const [modale, setModale] = useState<'park' | 'force' | 'main' | null>(null)
  const [motif, setMotif] = useState('')
  const [motifLibre, setMotifLibre] = useState('')
  const [tic, setTic] = useState(0)
  /** Clé de l'action serveur en cours : elle rend son bouton inerte (data-encours). */
  const [travail, setTravail] = useState<string | null>(null)
  /** Code de l'étape dont le bandeau de reprise a été lu — il a alors fait son office. */
  const [repriseVue, setRepriseVue] = useState<string | null>(null)
  /** Les tampons Honeywell livrés — lus une fois pour le bandeau ET pour le panneau. */
  const [tampons, setTampons] = useState<SessionEnAttente[]>([])

  const { date, etapes, journee, verrou, chargement, erreur } = run

  // Là où le travail reprend : la première actionnable, sinon la première dette
  // mise de côté (elle revient en fin de parcours), sinon le début.
  const defaut: Etape | undefined = useMemo(() => {
    const liste = [...etapes].sort(parRang)
    return liste.find((e) => estActionnable(e.statut))
      ?? liste.find((e) => estDeCote(e.statut))
      ?? liste[0]
  }, [etapes])

  // Étape courante : celle demandée par l'URL, sinon celle par défaut. Un code
  // inconnu — marque-page d'une autre journée, adresse tapée de travers — ne
  // doit pas produire un écran mort annonçant « cette run n'a pas d'étapes » :
  // on retombe sur l'étape par défaut et on corrige l'adresse.
  const courante: Etape | undefined =
    (codeDemande ? etapes.find((e) => e.code === codeDemande) : undefined) ?? defaut

  useEffect(() => {
    if (courante && codeDemande !== courante.code) {
      aller({ vue: 'run', date, etape: courante.code }, true)
    }
  }, [codeDemande, courante, date])

  // Entrer dans une étape la met « en cours » — une seule fois, tracée une fois.
  // Le verrou LIBRE n'empêche pas d'entrer : une écriture reprend en silence un
  // verrou expiré (D3), donc exiger « moi » laissait l'étape « à faire » pendant
  // qu'on la travaillait — après une expiration au coffre, ou en arrivant par
  // une adresse directe sans passer par l'accueil. Seule une run tenue par
  // quelqu'un d'autre, ou clôturée, ne se marque pas.
  //
  // ET SURTOUT : une étape qui pose une question d'entrée n'est jamais activée
  // d'office. Sa question EST la porte (P5). `fc_etape_activer` la faisait
  // passer en « en cours » dès l'arrivée, donc le formulaire — rendu sous
  // condition `statut === 'a_faire'` — disparaissait au rendu suivant : la
  // question de l'étape 02 clignotait et s'effaçait, et la réponse « non »,
  // seul chemin vers une non-applicabilité tracée, devenait inatteignable.
  useEffect(() => {
    if (!courante || chargement) return
    if (verrou.etat === 'occupee' || run.run?.statut === 'cloturee') return
    if (ETAPE_PAR_CODE.get(courante.code)?.question && courante.statut === 'a_faire') return
    void run.activer(courante.code)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courante?.code, courante?.statut, chargement, verrou.etat, run.run?.statut])

  // Chrono discret : la base vient du serveur, le tic est local.
  useEffect(() => { setTic(0) }, [run.run?.duree_cumulee_s])
  useEffect(() => {
    if (verrou.etat !== 'moi' || run.run?.statut !== 'ouverte') return
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') setTic((v) => v + 1)
    }, 1000)
    return () => window.clearInterval(t)
  }, [verrou.etat, run.run?.statut])

  // AVANT les retours anticipés — la règle des hooks ne souffre aucune exception,
  // et ce fichier a déjà rendu un écran blanc pour l'avoir oubliée une fois.
  const relireTampons = useCallback(async () => {
    try { setTampons(await api.hwEnAttente(date)) } catch { /* le bandeau se tait */ }
  }, [date])
  useEffect(() => { void relireTampons() }, [relireTampons, courante?.code])

  const naviguer = useCallback((code: string | 'bilan') => {
    if (code === 'bilan') aller({ vue: 'bilan', date })
    else aller({ vue: 'run', date, etape: code })
  }, [date])

  // Aucun écran ne se termine en cul-de-sac : une erreur et une run sans étapes
  // disent toutes deux ce qu'on peut faire ensuite.
  if (chargement) return <div className="wrap"><div className="empty">Chargement de la journée…</div></div>
  if (erreur) {
    return (
      <div className="wrap" style={{ paddingTop: 18 }}>
        <Notice c="stop" action={
          <>
            <button className="btn sm" onClick={() => void run.recharger()}>Réessayer</button>{' '}
            <button className="btn gh sm" onClick={() => aller({ vue: 'accueil' })}>
              Revenir à l’accueil
            </button>
          </>
        }>
          <b>La journée n’a pas pu être chargée</b><br />{erreur}
        </Notice>
      </div>
    )
  }
  if (!courante) {
    return (
      <div className="wrap" style={{ paddingTop: 18 }}>
        <Notice c="warn" action={
          <>
            <button className="btn sm" onClick={() => void run.recharger()}>Recharger la run</button>{' '}
            <button className="btn gh sm" onClick={() => aller({ vue: 'accueil' })}>
              Revenir à l’accueil
            </button>
          </>
        }>
          <b>Cette run n’a pas d’étapes</b> — le modèle des onze étapes n’a pas été posé.
          Recharger la run le repose ; si rien ne vient, l’ouverture de la journée a échoué.
        </Notice>
      </div>
    )
  }

  const modele = ETAPE_PAR_CODE.get(courante.code) ?? ETAPES[0]!
  const p = progression(etapes)
  const liste = [...etapes].sort(parRang)
  const idx = liste.findIndex((e) => e.code === courante.code)
  const prec = idx > 0 ? liste[idx - 1] : undefined
  const suiv = idx < liste.length - 1 ? liste[idx + 1] : undefined

  // Deux notions qu'on confondait, et la confusion coûtait des deux côtés :
  //   • FERMÉE — la run n'accepte plus aucune écriture (clôturée, ou tenue par
  //     quelqu'un d'autre). C'est elle qui interdit de ROUVRIR une étape ;
  //   • LECTURE SEULE — l'étape affichée ne se modifie pas, parce qu'elle est
  //     validée ou que la run est fermée.
  // Avant, « rouvrir quand même » d'une étape non applicable restait cliquable
  // sur une run clôturée ou occupée : un `lectureSeule` contourné.
  const occupee = verrou.etat === 'occupee' ? verrou : null
  const fermee = run.run?.statut === 'cloturee' || occupee !== null
  const lectureSeule = fermee || courante.statut === 'validee'

  // `courante` part avec le rapport : la complétude de l'étape 11 se lit dans
  // l'avancement de l'étape, pas dans le blob V2. Sans elle, sa lampe restait
  // ambre quoi qu'on imprime et toute validation passait par le forçage.
  //
  // Les prélèvements SCO gardent leur indicateur propre : ils ne se mesurent
  // pas en règlements vérifiés mais en enveloppes scellées.
  const indicateur: Pret = courante.code === '06'
    ? pretSco(journee?.rapport ?? null)
    : pret(courante.code, journee?.rapport ?? null, courante)
  const rep = reprise(courante)
  const secondes = (run.run?.duree_cumulee_s ?? 0) + tic
  // Les motifs proposés viennent des réglages ; « Autre » est toujours ajouté
  // en dernier — c'est le seul chemin vers un motif non prévu.
  //
  // PAS de `useMemo` : on est ici APRÈS les retours anticipés du chargement et
  // de l'étape absente. Un hook posé après un `return` n'est pas appelé à tous
  // les rendus, et React refuse de rendre — c'est exactement ce qui empêchait
  // d'ouvrir une journée. Concaténer quatre chaînes ne se mémoïse pas.
  const motifs = [...reglages.motifsDeCote, AUTRE]
  const motifPark = (motif === AUTRE ? motifLibre : motif).trim()

  // ── Actions ──────────────────────────────────────────────────────────────

  const avancer = () => naviguer(prochaineEtape(etapes, courante.code))

  /**
   * Une action qui attend le serveur : son bouton devient inerte le temps de
   * l'appel (`data-encours`), et un refus métier est déjà annoncé par le
   * provider — le laisser remonter produisait une promesse rejetée sans
   * receveur à chaque « non », chaque réouverture, chaque prise de main.
   */
  const agir = async (cle: string, action: () => Promise<unknown>) => {
    if (travail) return
    setTravail(cle)
    try { await action() } catch { /* le message d'erreur est déjà posé */ }
    finally { setTravail(null) }
  }

  const valider = async (force: boolean) => {
    if (travail) return
    setTravail('valider')
    try {
      await run.valider(courante.code, force)
      toast(`ÉTAPE ${courante.code} VALIDÉE${force ? ' EN FORÇANT' : ''} — ÉTAPE SUIVANTE`)
      avancer()
    } catch { /* le message d'erreur est déjà posé par le provider */ }
    finally { setTravail(null) }
  }

  /**
   * Le motif ne se recycle pas d'une étape sur l'autre. Il restait en état :
   * celui saisi à l'étape 03 se représentait pré-rempli à l'étape 07, et comme
   * « Entrée » valide dès qu'un motif est là, il repartait tel quel — au bilan
   * et dans le briefing de demain, sous le nom de la mauvaise étape.
   */
  const ouvrirPark = () => {
    setMotif(motifs[0] ?? '')
    setMotifLibre('')
    setModale('park')
  }

  /**
   * Une consigne visée sur CETTE étape se solde ici. La renvoyer au bilan, deux
   * écrans plus loin, c'est la faire oublier : elle repartait dans le briefing
   * de demain alors qu'elle venait d'être faite. `api.majConsigne` n'est pas
   * passée par le provider — son erreur n'est annoncée que si on l'annonce.
   */
  const consigneFaite = async (id: string) => {
    if (travail) return
    setTravail(id)
    try {
      await api.majConsigne(id, 'faite', nom)
      await run.recharger()
      toast('CONSIGNE MARQUÉE FAITE')
    } catch (e) { toast(messageErreur(e), true) }
    finally { setTravail(null) }
  }

  const mettreDeCote = async (arreter: boolean) => {
    if (!motifPark) { toast('UN MOTIF EST OBLIGATOIRE', true); return }
    if (travail) return
    setTravail(arreter ? 'arret' : 'cote')
    try {
      await run.mettreDeCote(courante.code, motifPark)
      setModale(null)
      if (arreter) {
        await run.liberer()
        aller({ vue: 'accueil' })
        toast('PROGRESSION SAUVEGARDÉE — RUN LIBÉRÉE')
      } else {
        toast(`ÉTAPE ${courante.code} MISE DE CÔTÉ — ELLE REVIENDRA EN FIN DE PARCOURS`)
        avancer()
      }
    } catch { /* déjà signalé */ }
    finally { setTravail(null) }
  }

  const Corps = ECRANS[courante.code]

  return (
    <>
      {/* ── bandeau ─────────────────────────────────────────────────────── */}
      <div className="band">
        <div className="wrap band-in">
          <button className="b-prev" onClick={() => prec && naviguer(prec.code)} disabled={!prec}>
            {prec ? `◀ ${prec.code} ${prec.libelle}${prec.statut === 'validee' ? ' ✓' : estSkip(prec.statut) ? ' ⌀' : ''}` : ''}
          </button>
          <div className="b-cur">
            <span className="num">{courante.code}</span>
            <span className="nm">
              {courante.libelle}
            </span>
          </div>
          <button className="b-next" onClick={() => naviguer(suiv ? suiv.code : 'bilan')}>
            {suiv ? `${suiv.code} ${suiv.libelle}${estSkip(suiv.statut) ? ' ⌀' : ''} ▶` : 'BILAN ▶'}
          </button>
          <span className="sp" />
          <div className="b-seg" aria-hidden="true">
            {liste.map((e) => <i key={e.code} className={segment(e, courante.code)} />)}
          </div>
          <div className="b-stat" title="Temps réellement passé dans la run">⏱ {duree(secondes)}</div>
          {p.deCote > 0 && <div className="b-stat"><Chip c="warn">{p.deCote} DE CÔTÉ</Chip></div>}
          {/* Un tampon livré depuis le terminal n'apparaît que sur SON étape.
              Sans ce rappel, celui qui compte le coffre puis revient au
              dépouillement ne le retrouve qu'en repassant par l'étape 10 —
              c'est-à-dire, en pratique, jamais. */}
          {tampons.length > 0 && (
            <div className="b-stat">
              <button className="btn hw sm"
                      title="Un tampon Honeywell attend d’être révisé puis versé"
                      onClick={() => {
                        const t = tampons[0]
                        if (t) naviguer(ETAPE_DU_MODULE[t.module])
                      }}>
                {tampons.length} TAMPON{tampons.length > 1 ? 'S' : ''} À VERSER ·{' '}
                {tampons.map((t) => LIBELLE_MODULE[t.module].toUpperCase()).join(' + ')}
              </button>
            </div>
          )}
          <button className="btn sm" onClick={() => setRailOuvert((v) => !v)}
                  aria-expanded={railOuvert}>Rail {railOuvert ? '⌃' : '⌄'}</button>
        </div>

        {railOuvert && (
          <div className="rail">
            <div className="wrap">
              <div className="rail-in">
                {liste.map((e) => (
                  <button className={[
                    'rl',
                    e.code === courante.code ? 'is-cur' : '',
                    e.statut === 'validee' ? 'is-done' : '',
                    estSkip(e.statut) ? 'is-skip' : '',
                    e.statut === 'de_cote' ? 'is-park' : '',
                  ].join(' ')} key={e.code}
                          onClick={() => { setRailOuvert(false); naviguer(e.code) }}>
                    <span className="n">{e.code}</span>
                    <span>
                      <span className="nm">{e.libelle}</span>
                      <span className="s">{etatRail(e)}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── corps ───────────────────────────────────────────────────────── */}
      <div className="wrap step">
        <div className="st-head">
          <h1 className="st-title">
            <small>ÉTAPE {courante.code} SUR {liste.length} · {modele.sous}</small>
            {courante.libelle}
          </h1>
          <div className="tools">
            {/* La liste qui fait foi est celle des modules RÉELLEMENT
                construits (domain/hw), pas celle que le modèle d'étape
                annonce : 04 et 08 en déclarent un que le terminal n'a pas. */}
            {modulesDeLEtape(courante.code).length > 0 && <Chip c="hw">HONEYWELL DISPO</Chip>}
            {courante.statut === 'de_cote' && <Chip c="warn">MISE DE CÔTÉ</Chip>}
            {courante.force_valide && <Chip c="warn">VALIDÉE EN FORÇANT</Chip>}
            {/* Relire le serveur À LA DEMANDE. La run est chargée une fois à
                l'entrée, puis la navigation se fait en mémoire (§2.1) : quand
                quelqu'un livre un tampon depuis le terminal ou saisit depuis un
                autre poste, rien ne bouge ici. On rechargeait la page entière
                pour le voir, ce qui fait repayer tout le chargement. */}
            <button className="btn gh sm" title="Relire la journée et les tampons Honeywell"
                    data-encours={travail === 'relire' ? 'true' : undefined}
                    onClick={() => void agir('relire', async () => {
                      await run.recharger()
                      await relireTampons()
                    })}>
              ⟳ Recharger
            </button>
            <button className="btn gh sm" onClick={() => aller({ vue: 'journal', date })}>Journal</button>
          </div>
        </div>

        {/* Le verrou tenu par un autre laisse toujours deux sorties : prendre la
            main — un geste explicite, jamais un effet de bord (D3) — ou repartir. */}
        {occupee && (
          <Notice c="warn" action={
            <>
              <button className="btn stop sm" onClick={() => setModale('main')}>
                Prendre la main
              </button>{' '}
              <button className="btn gh sm" onClick={() => aller({ vue: 'accueil' })}>
                Revenir à l’accueil
              </button>
            </>
          }>
            <b>{occupee.par}</b> est dans cette run depuis {heure(occupee.depuis)} — une seule
            personne à la fois. Tu la consultes en lecture seule.
          </Notice>
        )}

        {run.run?.statut === 'cloturee' && (
          <Notice action={
            <button className="btn sm" onClick={() => naviguer('bilan')}>Ouvrir le bilan</button>
          }>
            <b>Journée clôturée</b> — lecture seule. Le bilan reste consultable.
          </Notice>
        )}

        {/* Validée EN FORÇANT n'est pas validée : l'étape a passé outre ses
            contrôles. Le bandeau le disait en toutes lettres mais restait vert —
            or c'est la lampe qu'on lit, pas la phrase. Le bilan, lui, refuse
            déjà le vert à une étape forcée : les deux écrans doivent s'accorder,
            sinon l'un des deux ment. */}
        {courante.statut === 'validee' && (
          <Notice c={courante.force_valide ? 'warn' : 'ok'} action={
            <button className="btn sm" disabled={fermee}
                    data-encours={travail === 'rouvrir' ? 'true' : undefined}
                    title={fermee ? 'La run est clôturée ou tenue par quelqu’un d’autre' : undefined}
                    onClick={() => void agir('rouvrir', () => run.rouvrir(courante.code))}>
              Rouvrir l’étape
            </button>
          }>
            <b>Étape validée</b> par {courante.valide_par_nom ?? '—'} à {heure(courante.valide_at)}
            {courante.force_valide && ' — validée malgré les contrôles'} — lecture seule.
          </Notice>
        )}

        {rep && courante.statut !== 'validee' && repriseVue !== courante.code && (
          <Notice c="act" action={
            <button className="btn sm" onClick={() => setRepriseVue(courante.code)}>
              Reprendre ici
            </button>
          }>
            <b>Tu as quitté cette étape il y a {depuis(rep.absenceMs)}</b> — dernière activité à{' '}
            {heure(rep.depuis)}.{' '}
            {resumeAvancement(courante, indicateur)}
          </Notice>
        )}

        {/* Une consigne posée sur cette étape se solde depuis cette étape. */}
        {run.consignes.filter((c) => c.etape_cible === courante.code && c.statut === 'a_faire').map((c) => (
          <Notice key={c.id} c={c.urgence === 3 ? 'stop' : c.urgence === 2 ? 'warn' : 'neutre'}
                  action={fermee ? undefined : (
                    <button className="btn ok sm" data-encours={travail === c.id ? 'true' : undefined}
                            onClick={() => void consigneFaite(c.id)}>
                      Marquer faite
                    </button>
                  )}>
            <b>Consigne de {c.auteur_nom ?? '—'}</b> — {c.texte}
          </Notice>
        ))}

        {/* Le tampon du terminal revient ICI — c'est le seul endroit où il
            entre dans la journée, et le seul où quelqu'un le relit d'abord. */}
        <PanneauVersement date={date} code={courante.code} lectureSeule={lectureSeule}
                          attente={tampons}
                          apresVersement={async () => { await run.recharger(); await relireTampons() }} />

        {/* Question d'entrée — on ne demande QUE ce qui n'est pas déductible (P5). */}
        {modele.question && courante.statut === 'a_faire' && !lectureSeule
          && !questionSansObjet(courante.code, journee?.rapport ?? null) ? (
          <div className="gate">
            <div className="q">{modele.question.texte}</div>
            <div className="sub">{modele.question.pourquoi}</div>
            <div className="btns">
              <button className="btn pri lg" data-encours={travail === 'oui' ? 'true' : undefined}
                      onClick={() => void agir('oui', () => run.repondre(courante.code, true))}>
                Oui — continuer
              </button>
              {/* « Non » écarte l'étape : c'est un renoncement tracé, pas une
                  simple navigation — et il reste rouvrable. */}
              <button className="btn stop" data-encours={travail === 'non' ? 'true' : undefined}
                      onClick={() => void agir('non',
                        () => run.repondre(courante.code, false).then(avancer))}>
                Non — passer l’étape
              </button>
            </div>
            <div className="hint" style={{ marginTop: 12 }}>
              Une réponse « non » est tracée comme non-applicabilité, pas comme dette —
              et reste rouvrable si elle était une erreur.
            </div>
          </div>
        ) : estSkip(courante.statut) ? (
          <Notice c="warn" action={
            <button className="btn sm" disabled={fermee}
                    data-encours={travail === 'rouvrir' ? 'true' : undefined}
                    title={fermee ? 'La run est clôturée ou tenue par quelqu’un d’autre' : undefined}
                    onClick={() => void agir('rouvrir', () => run.rouvrir(courante.code))}>
              Rouvrir quand même
            </button>
          }>
            <b>Étape non applicable aujourd’hui</b><br />
            {courante.motif_regle ?? courante.motif ?? 'Réponse « non » à la question d’entrée.'}
            <div className="hint" style={{ marginTop: 3 }}>
              TRACÉE COMME NON APPLICABLE — CE N’EST PAS UNE DETTE
            </div>
          </Notice>
        ) : (
          <>
            {courante.statut === 'de_cote' && (
              <Notice c="warn">
                <b>Étape mise de côté</b> — motif : {courante.motif ?? '—'}. Elle est revenue avant
                le bilan : c’est une dette, pas une non-applicabilité.
              </Notice>
            )}
            {Corps
              ? <Corps etape={courante} modele={modele} rapport={journee?.rapport ?? null}
                       lectureSeule={lectureSeule} />
              : <div className="empty">Écran à définir.</div>}
          </>
        )}
      </div>

      {/* ── barre d'action ──────────────────────────────────────────────── */}
      <div className="stbar">
        <div className="wrap stbar-in">
          {courante.statut === 'validee' ? (
            <span className="ready">
              <Lamp c={courante.force_valide ? 'warn' : 'ok'} />
              {courante.force_valide ? 'Validée en forçant' : 'Validée'} à {heure(courante.valide_at)}
              {courante.force_valide && <span className="mo">· contrôles non satisfaits</span>}
            </span>
          ) : estSkip(courante.statut) ? (
            <span className="ready"><Lamp c="warn" />Non applicable</span>
          ) : (
            <span className="ready">
              <Lamp c={indicateur.ok ? 'ok' : 'warn'} />
              {indicateur.ok ? 'Prêt à valider' : 'Contrôles incomplets'}
              <span className="mo">· {indicateur.texte}</span>
            </span>
          )}
          <span className="sp" />
          {/* Différer, revenir, valider : trois tons, trois sens. La validation
              est l'action de l'écran — la plus grande et la plus à droite, là
              où la main va d'elle-même. */}
          {!lectureSeule && !estSkip(courante.statut) && (
            <button className="btn warn" onClick={ouvrirPark}>Mettre de côté</button>
          )}
          <button className="btn gh" onClick={() => prec && naviguer(prec.code)} disabled={!prec}
                  aria-label={prec ? `Revenir à l’étape ${prec.code}` : 'Aucune étape précédente'}
                  title={prec ? `Revenir à l’étape ${prec.code} ${prec.libelle}` : undefined}>←</button>
          {courante.statut === 'validee' || estSkip(courante.statut) ? (
            <button className="btn pri lg" onClick={avancer}>
              {suiv ? `Étape suivante ${suiv.code} →` : 'Ouvrir le bilan →'}
            </button>
          ) : (
            <button className="btn pri lg" disabled={lectureSeule}
                    data-encours={travail === 'valider' ? 'true' : undefined}
                    onClick={() => (indicateur.ok ? void valider(false) : setModale('force'))}
                    title={indicateur.ok ? undefined : `Contrôles incomplets : ${indicateur.texte}`}>
              Valider l’étape {courante.code} →
            </button>
          )}
        </div>
      </div>

      <Terminal date={date} enLigne={run.enLigne} attente={run.attente} />

      {modale === 'park' && (
        <Modale titre="Mettre l’étape de côté"
                sous="Elle revient en fin de parcours, avant le bilan. Le motif part au bilan et au briefing de demain."
                fermer={() => setModale(null)}>
          {/* Entrée met de côté et continue, Échap referme (via <Modale>). */}
          <div onKeyDown={(e) => {
            if (e.key === 'Enter' && motifPark) { e.preventDefault(); void mettreDeCote(false) }
          }}>
            <label className="lbl" htmlFor="motif">Motif</label>
            <select id="motif" value={motif} autoFocus onChange={(e) => setMotif(e.target.value)}>
              {motifs.map((m) => <option key={m}>{m}</option>)}
            </select>
            {motif === AUTRE && (
              <input placeholder="Motif" value={motifLibre} autoFocus
                     onChange={(e) => setMotifLibre(e.target.value)} />
            )}
          </div>
          <div className="btns">
            <button className="btn" onClick={() => setModale(null)}>Annuler</button>
            <button className="btn" disabled={!motifPark}
                    data-encours={travail === 'arret' ? 'true' : undefined}
                    onClick={() => void mettreDeCote(true)}>M’arrêter là</button>
            <button className="btn warn" disabled={!motifPark}
                    data-encours={travail === 'cote' ? 'true' : undefined}
                    title={motifPark ? undefined : 'Le motif est obligatoire'}
                    onClick={() => void mettreDeCote(false)}>
              Mettre de côté et continuer
            </button>
          </div>
        </Modale>
      )}

      {modale === 'force' && (
        <Modale titre="Valider malgré les contrôles ?"
                sous={`${indicateur.texte} — la validation reste possible, elle sera tracée telle quelle au journal.`}
                fermer={() => setModale(null)}>
          <div className="btns">
            <button className="btn" onClick={() => setModale(null)}>Annuler</button>
            {/* Quand un contrôle manque, la bonne issue est le plus souvent la
                dette assumée, pas le passage en force. Elle n'était offerte
                nulle part ici : il fallait annuler, retrouver le bouton dans la
                barre, rouvrir une modale. Elle est là où la décision se prend. */}
            <button className="btn warn" onClick={ouvrirPark}>
              Mettre de côté plutôt
            </button>
            {/* Forcer, c'est passer outre : le ton le dit avant le clic. */}
            <button className="btn stop" data-encours={travail === 'valider' ? 'true' : undefined}
                    onClick={() => { setModale(null); void valider(true) }}>
              Valider l’étape {courante.code} en forçant
            </button>
          </div>
        </Modale>
      )}

      {modale === 'main' && occupee && (
        <Modale titre="Prendre la main sur cette run ?"
                sous={`${occupee.par} y travaille depuis ${heure(occupee.depuis)}. La prise de main lui retire l’écriture sans la prévenir — elle est inscrite au journal.`}
                fermer={() => setModale(null)}>
          <div className="btns">
            <button className="btn" onClick={() => setModale(null)}>Annuler</button>
            <button className="btn stop" data-encours={travail === 'main' ? 'true' : undefined}
                    onClick={() => { setModale(null); void agir('main', () => run.prendreMain(true)) }}>
              Prendre la main
            </button>
          </div>
        </Modale>
      )}
    </>
  )
}

function etatRail(e: Etape): string {
  switch (e.statut) {
    case 'validee':       return `VALIDÉE ${heure(e.valide_at)} · ${initiales(e.valide_par_nom)}`
    case 'skip_regle':    return 'NON APPLICABLE · RÈGLE'
    case 'skip_question': return 'NON APPLICABLE · RÉPONSE'
    case 'de_cote':       return `DE CÔTÉ · ${(e.motif ?? '').toUpperCase().slice(0, 26)}`
    case 'en_cours':      return 'EN COURS'
    default:              return 'À FAIRE'
  }
}

/**
 * Où en était l'étape quand tu l'as quittée.
 *
 * L'avancement FlashCP ne porte que ce que les écrans y écrivent : les
 * documents cochés (11) et les dépôts de fichiers (01). Tout le reste du
 * travail part dans le blob V2. Annoncer « rien n'a bougé depuis » à quelqu'un
 * qui venait de vérifier trente règlements était donc faux : l'indicateur de
 * complétude de l'étape, lui, dit toujours la vérité.
 */
function resumeAvancement(e: Etape, indicateur: Pret): string {
  const a = e.avancement ?? {}
  const docs = a.docs as Record<string, boolean> | undefined
  if (docs) {
    const n = Object.values(docs).filter(Boolean).length
    return `${pluriel(n, 'document')} déjà coché${n > 1 ? 's' : ''}.`
  }
  const imports = a.imports as Record<string, unknown> | undefined
  if (imports) {
    const n = Object.keys(imports).length
    return `${pluriel(n, 'fichier')} déjà déposé${n > 1 ? 's' : ''}.`
  }
  return `Où tu en es : ${indicateur.texte}.`
}

/** Présent, non fonctionnel dans ce build — point ouvert O4. */
function Terminal({ date, enLigne, attente }: { date: string; enLigne: boolean; attente: number }) {
  return (
    <div className="terminal">
      <div className="wrap in">
        <span className="pr">flashcp</span>
        <span>run {date}</span>
        <span className="sp" />
        {!enLigne && <span style={{ color: 'var(--warn-b)' }}>HORS LIGNE · {attente} EN ATTENTE</span>}
        <span style={{ opacity: .5 }}>terminal inactif — O4</span>
      </div>
    </div>
  )
}
